import type { CopyKey } from "./copy";
import type {
  OwnerDeviceState,
  ShareState,
  TransferState,
  VideoCapabilityState,
  ViewerActivityState,
  ViewerAdmissionState,
} from "./states";

export type PresentationTone =
  | "neutral"
  | "informational"
  | "attention"
  | "success"
  | "error";

export interface StatePresentation {
  readonly copyKey: CopyKey | null;
  readonly tone: PresentationTone;
  readonly announce: boolean;
}

export function selectOwnerDevicePresentation(
  state: OwnerDeviceState,
): StatePresentation {
  switch (state) {
    case "available":
      return present("owner.device.available", "success", false);
    case "reconnecting":
      return present("owner.device.reconnecting", "informational", true);
    case "background-limited":
      return present("owner.device.backgroundLimited", "attention", true);
    case "unavailable":
      return present("owner.device.unavailable", "error", true);
    default:
      return assertNever(state);
  }
}

export function selectSharePresentation(
  state: ShareState,
): StatePresentation {
  switch (state) {
    case "draft":
      return present("share.draft", "neutral", false);
    case "preparing":
      return present("share.preparing", "informational", true);
    case "available":
      return present("share.available", "success", false);
    case "active":
      return present("share.active", "success", true);
    case "paused":
      return present("share.paused", "neutral", true);
    case "needs-photos":
      return present("share.needsPhotos", "attention", true);
    case "expired":
      return present("share.expired", "neutral", true);
    case "revoked":
      return present("share.revoked", "error", true);
    default:
      return assertNever(state);
  }
}

export function selectViewerAdmissionPresentation(
  state: ViewerAdmissionState,
): StatePresentation {
  switch (state) {
    case "authorizing":
      return present("viewer.admission.authorizing", "informational", true);
    case "waiting-for-owner":
      return present(
        "viewer.admission.waitingForOwner",
        "informational",
        true,
      );
    case "waiting-for-capacity":
      return present(
        "viewer.admission.waitingForCapacity",
        "informational",
        true,
      );
    case "connecting":
      return present("viewer.admission.connecting", "informational", true);
    case "connected":
      return present(null, "neutral", false);
    case "reconnecting":
      return present("viewer.admission.reconnecting", "informational", true);
    case "unavailable":
      return present("viewer.admission.unavailable", "error", true);
    default:
      return assertNever(state);
  }
}

export function selectViewerActivityPresentation(
  state: ViewerActivityState,
): StatePresentation {
  switch (state) {
    case "ready":
      return present("viewer.activity.ready", "neutral", false);
    case "browsing":
      return present("viewer.activity.browsing", "neutral", false);
    case "downloading-original":
      return present(
        "viewer.activity.downloadingOriginal",
        "informational",
        true,
      );
    default:
      return assertNever(state);
  }
}

export function selectTransferPresentation(
  state: TransferState,
): StatePresentation {
  switch (state) {
    case "queued":
      return present("transfer.queued", "informational", true);
    case "starting":
      return present("transfer.starting", "informational", true);
    case "transferring":
      return present("transfer.transferring", "informational", false);
    case "verifying":
      return present("transfer.verifying", "informational", true);
    case "completed":
      return present("transfer.completed", "success", true);
    case "cancelled":
      return present("transfer.cancelled", "neutral", true);
    case "failed":
      return present("transfer.failed", "error", true);
    default:
      return assertNever(state);
  }
}

export interface VideoCapabilitySelectionInput {
  readonly globalPolicyEnabled?: boolean;
  readonly ownerCohortEnabled?: boolean;
  readonly ownerClientSupports?: boolean;
  readonly viewerClientSupports?: boolean;
  readonly access?: "view" | "download";
  readonly rollout?: "testing" | "available";
  readonly disabledDuringSession?: boolean;
}

export function selectVideoCapabilityState(
  input: VideoCapabilitySelectionInput,
): VideoCapabilityState {
  if (input.disabledDuringSession === true) {
    return "disabled-during-session";
  }
  if (
    input.globalPolicyEnabled !== true
    || input.ownerCohortEnabled !== true
    || input.ownerClientSupports !== true
    || input.viewerClientSupports !== true
    || input.access !== "download"
  ) {
    return "hidden";
  }
  return input.rollout === "available" ? "available" : "testing";
}

export function selectVideoCapabilityPresentation(
  state: VideoCapabilityState,
): StatePresentation {
  switch (state) {
    case "hidden":
    case "testing":
    case "available":
      return present(null, "neutral", false);
    case "disabled-during-session":
      return present("video.disabledDuringSession", "attention", true);
    default:
      return assertNever(state);
  }
}

export function canRenderVideo(
  state: VideoCapabilityState,
  access: "view" | "download",
): boolean {
  return access === "download"
    && (state === "testing" || state === "available");
}

function present(
  copyKey: CopyKey | null,
  tone: PresentationTone,
  announce: boolean,
): StatePresentation {
  return { copyKey, tone, announce };
}

function assertNever(value: never): never {
  throw new TypeError(`Unhandled product state: ${String(value)}`);
}
